import * as z from "zod";
import { extendZodWithOpenApi } from "@hono/zod-openapi";

extendZodWithOpenApi(z);

// Pagination parameters schema
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  sort: z.string().optional(),
});

type PaginationParams = z.infer<typeof paginationSchema>;

export const paginationMetaSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  total: z.number(),
  totalPages: z.number(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
});

// Parse sort string (e.g., "field:asc" or "field:desc")
const parseSortString = (
  sortString: string,
): { field: string; direction: "asc" | "desc" } | null => {
  const match = sortString.match(/^([^:]+):(asc|desc)$/);
  if (!match) return null;

  const [, field, direction] = match;
  return {
    field,
    direction: direction as "asc" | "desc",
  };
};

// Convert pagination params to Prisma options
export const paginationToPrisma = (
  params: PaginationParams,
  allowedSortFields: string[] = [],
  defaultSort: { [key: string]: "asc" | "desc" } = {},
) => {
  const skip = (params.page - 1) * params.pageSize;
  const take = params.pageSize;

  let orderBy = defaultSort;

  if (params.sort) {
    const parsed = parseSortString(params.sort);
    if (parsed && allowedSortFields.includes(parsed.field)) {
      orderBy = { [parsed.field]: parsed.direction };
    }
  }

  return {
    skip,
    take,
    orderBy,
  };
};

// Create paginated response metadata
export const createPaginationMeta = (
  page: number,
  pageSize: number,
  total: number,
) => {
  const totalPages = Math.ceil(total / pageSize);

  return {
    page,
    pageSize,
    total,
    totalPages,
    hasNext: page < totalPages,
    hasPrev: page > 1,
  };
};
